/* eslint-disable react-hooks/exhaustive-deps */
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import FlagsSelect from '@orientaction/components/CountryFlagSelector/FlagsSelect';
import { useOfflineLanguage } from '@orientaction/hooks';
import { isAdmin, publicIconPath } from '@orientaction/utils';
import { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { UserProfilePhotoContext } from '../../../../Context/UserProfilePhotoContext';
import config from '../../../../services/config';
import { connectUser, modifyUser } from '../../../../services/user.service';
import { emailMatch } from '../../../../utils/constants';
import { apiServiceNative } from '../../../../utils/fetchNative';
import { getAppleConnectUrl } from '../../../../utils/sso';
import RedesignImage from '../../shared/components/RedesignImage';
import { useAuthStyles } from '../Auth.styles';
import HelpModal from '../components/HelpModal';
import locale from './locale.json';
import { useLoginFormStyles } from './LoginForm.styles';

const PASSWORD_PATTERN =
  /^(((?=.*[!@#$%^&*])(?=.*[A-Z])(?=.*[a-z]))|((?=.*\d)(?=.*[A-Z])(?=.*[a-z]))|((?=.*\d)(?=.*[!@#$%^&*])(?=.*[a-z]))|((?=.*\d)(?=.*[!@#$%^&*])(?=.*[A-Z]))).{8,}$/;

const LoginForm = () => {
  const shared = useAuthStyles();
  const local = useLoginFormStyles();
  const cls = { ...shared, ...local };
  const [isLoading, setIsLoading] = useState(false);
  const [eyeOn, setEyeOn] = useState(false);
  const [connectError, setConnectError] = useState('');
  const [helpOpen, setHelpOpen] = useState(false);

  const { language, onLanguageChange } = useOfflineLanguage();
  const history = useHistory();
  const { search } = useLocation();
  const { resetPhoto, updateUserProfilePhoto } = useContext<any>(UserProfilePhotoContext);

  const {
    control,
    handleSubmit,
  } = useForm({ mode: 'onChange' });

  useEffect(() => {
    (async () => {
      const userIdToConfirm = new URLSearchParams(search).get('confirmation');
      if (userIdToConfirm) {
        await modifyUser({ id: userIdToConfirm, confirmed: true });
      }
    })();
  }, []);

  const onSubmitHandler = async (data: any) => {
    setIsLoading(true);
    const idGameResult = new URLSearchParams(search).get('idResult');

    try {
      resetPhoto();
      const connectData = await connectUser({ ...data, identifier: data.email });
      if (connectData?.data?.jwt) {
        localStorage.setItem('userInfos', JSON.stringify(connectData?.data?.user));
        localStorage.setItem('userToken', connectData?.data?.jwt);
        updateUserProfilePhoto();

        if (connectData?.data?.user?.postConfirmationRedirect) {
          history.push(connectData?.data?.user?.postConfirmationRedirect);
          await apiServiceNative.put(`/api/users/${connectData?.data?.user.id}`, {
            postConfirmationRedirect: null,
          });
        } else if (idGameResult) {
          await apiServiceNative.put(`/api/update-result-game/${idGameResult}`, {
            user_id: connectData?.data?.user.id,
          });
          history.push(`/game/play/${idGameResult}/0`);
        } else if (isAdmin(connectData?.data?.user)) {
          const services =
            connectData?.data?.user?.allowed_services?.map((i: { id: string }) => i.id) ?? [];
          if (services.length === 1 && services[0] === 4) {
            history.push('/admin/emploi/board');
            return;
          }
          history.push('admin/users-resultats');
        } else {
          history.push('/main');
        }
      }
    } catch (error: any) {
      if (error?.response?.data?.error?.name === 'ValidationError') {
        setConnectError(
          "Une erreur s'est produite. Vérifier que vous avez rentré correctement vos identifiants"
        );
      } else if (error.response?.data?.error?.name === 'NotFoundError') {
        setConnectError('Adresse e-mail ou mot de passe incorrect. Veuillez réessayer.');
      } else {
        setConnectError('Une erreur est survenue');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div className={cls.headingGroup}>
        <h1 className={cls.title}>{locale.loginFormHeaderTitle}</h1>
        <p className={cls.subtitle}>{locale.loginFormHeaderSubtitle}</p>
      </div>

      <Box className={cls.languageSelector}>
        <FlagsSelect countries={['FR', 'GB']} selected={language} onSelect={onLanguageChange} classes={{button: cls.languageSelectorButton}}/>
      </Box>

      <form onSubmit={handleSubmit(onSubmitHandler)} className={cls.form}>
        <div className={cls.fieldsGroup}>
          <Controller
            name="email"
            control={control}
            defaultValue=""
            rules={{
              required: 'Champ obligatoire',
              pattern: {
                value: emailMatch,
                message: 'Veuillez saisir une adresse e-mail valide',
              },
            }}
            render={({ field, fieldState }) => (
              <label htmlFor="login-email" className={cls.field}>
                <span className={cls.label}>{locale.email}</span>
                <div className={cls.inputWrapper}>
                  <input
                    {...field}
                    id="login-email"
                    type="text"
                    placeholder={locale.emailPlaceholder}
                    className={`${cls.input} ${cls.inputWithIcon}`}
                  />
                  <RedesignImage
                    src={`${publicIconPath}/Email.svg`}
                    alt=""
                    className={cls.inputIcon}
                    style={{ cursor: 'default' }}
                  />
                </div>
                {fieldState.error?.message && (
                  <span className={cls.errorText}>{fieldState.error.message}</span>
                )}
              </label>
            )}
          />

          <Controller
            name="password"
            control={control}
            defaultValue=""
            rules={{
              required: 'Champ obligatoire',
              pattern: {
                value: PASSWORD_PATTERN,
                message:
                  'Votre mot de passe doit contenir au moins 8 caractères et au moins 3 des éléments suivants :\n- Lettres minuscules (a-z)\n- Lettres majuscules (A-Z)\n- Chiffres (0-9)\n- Caractères spéciaux (ex. !@#)}',
              },
            }}
            render={({ field, fieldState }) => (
              <label htmlFor="login-password" className={cls.field}>
                <span className={cls.label}>{locale.password}</span>
                <div className={cls.inputWrapper}>
                  <input
                    id="login-password"
                    {...field}
                    type={eyeOn ? 'text' : 'password'}
                    placeholder={locale.passwordPlaceholder}
                    className={`${cls.input} ${cls.inputWithIcon}`}
                  />
                  <button
                    type="button"
                    className={cls.inputIcon}
                    onClick={() => setEyeOn(prev => !prev)}
                    aria-label={eyeOn ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}>
                    <RedesignImage
                      src={`${publicIconPath}/${eyeOn ? 'EyeOpened' : 'EyeClosed'}.svg`}
                      alt=""
                    />
                  </button>
                </div>
                {fieldState.error?.message && (
                  <span className={`${cls.errorText} ${cls.errorTextMultiline}`}>
                    {fieldState.error.message}
                  </span>
                )}
              </label>
            )}
          />
        </div>

        <div className={cls.linkRight}>
          <Link className={cls.link} to="/forgot-password">
            {locale.forgotPasswordText}
          </Link>
        </div>

        {connectError && <p className={cls.errorTextMd}>{connectError}</p>}

        <Button
          type="submit"
          className={cls.primaryButton}
          disabled={isLoading}
          data-cy="login-user">
          {isLoading ? `${locale.connectText}...` : locale.connectText}
        </Button>
      </form>

      <div className={cls.divider}>
        <div className={cls.dividerLine} />
        <span className={cls.dividerText}>ou</span>
        <div className={cls.dividerLine} />
      </div>

      <div className={cls.ssoSection}>
        <button
          type="button"
          className={cls.outlineButton}
          onClick={() => {
            window.location.href = `${config.api_url}/api/connect/google`;
          }}>
          <RedesignImage src={`${publicIconPath}/Google.svg`} alt="" width={16} height={16} />
          <span className={cls.ssoButtonText}>{locale.ssoGoogle}</span>
        </button>
        <button
          type="button"
          className={cls.outlineButton}
          onClick={() => {
            window.location.href = `${config.api_url}/api/connect/facebook`;
          }}>
          <RedesignImage src={`${publicIconPath}/Facebook.svg`} alt="" width={16} height={16} />
          <span className={cls.ssoButtonText}>{locale.ssoFacebook}</span>
        </button>
        <button
          type="button"
          className={cls.outlineButton}
          onClick={() => {
            window.location.href = getAppleConnectUrl();
          }}>
          <RedesignImage src={`${publicIconPath}/Apple.svg`} alt="" width={16} height={16} />
          <span className={cls.ssoButtonText}>{locale.ssoApple}</span>
        </button>
      </div>

      <div className={cls.footerRow}>
        <span className={cls.footerText}>{locale.loginQuestionText}</span>
        <Link className={cls.linkPrimary} to="/signup">
          {locale.loginRedirectText}
        </Link>
      </div>

      <button type="button" className={cls.outlineButton} onClick={() => setHelpOpen(true)}>
        <RedesignImage src={`${publicIconPath}/Info.svg`} alt="" width={16} height={16} />
        <span className={cls.outlineButtonTextPrimary}>Besoin d&apos;aide ?</span>
      </button>
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </>
  );
};

export default LoginForm;
